import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Calendar, MapPin, Utensils, Star, Users, MessageCircle, ChefHat, Globe, Coffee, Loader, Upload, Trash2, AlertCircle, User, Award, Heart, Clock, Beer, Flame, DollarSign, Camera, PlusCircle, Settings } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';
import FriendRequestButton from '../components/FriendRequestButton';
import BackButton from '../components/BackButton';
import ReferralLink from '../components/ReferralLink';
import Friends from '../components/Friends';
import DinnerRecommendations from '../components/DinnerRecommendations';
import DinnerRecommendationButton from '../components/DinnerRecommendationButton';
import LoadingIndicator from '../components/LoadingIndicator';
import { useLanguage } from '../contexts/LanguageContext';
import Achievements from '../components/Achievements';
import { calculateAchievements } from '../lib/achievements';
import AchievementPoints from '../components/AchievementPoints';
import PendingFriendRequests from '../components/PendingFriendRequests';
import { getUserListings } from '../lib/listings';

type Profile = Database['public']['Tables']['profiles']['Row'];

const UserProfile = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { id: profileId } = useParams();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [achievements, setAchievements] = useState<any[]>([]);
  const [userListings, setUserListings] = useState<any[]>([]);
  const [loadingListings, setLoadingListings] = useState(false);
  const [activityStats, setActivityStats] = useState({
    dinnersAttended: 0,
    friendsCount: 0,
    blindDatesAttended: 0,
    daysActive: 0
  });
  const [animateStats, setAnimateStats] = useState(false);

  useEffect(() => {
    initialize();
  }, [profileId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimateStats(true);
    }, 500);
    return () => clearTimeout(timer);
  }, [activityStats]);

  const initialize = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError) throw authError;

      if (!session?.user) {
        if (!profileId) {
          navigate('/login');
          return;
        }
      } else {
        setCurrentUser(session.user);
      }

      const isOwn = !profileId || (session?.user && profileId === session.user.id);
      setIsOwnProfile(isOwn);

      const targetId = isOwn ? session?.user?.id : profileId;
      if (!targetId) {
        setError('No profile ID provided');
        setLoading(false);
        return;
      }

      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', targetId)
        .maybeSingle();

      if (profileError) {
        throw profileError;
      }

      if (!profile) {
        setError('Profile not found');
        setLoading(false);
        return;
      }

      setProfile(profile);

      if (isOwn && profile) {
        await loadActivityStats(profile.id);
        const userAchievements = await calculateAchievements(profile.id);
        setAchievements(userAchievements);
        
        await fetchUserListings();
      }
    } catch (err) {
      console.error('Error initializing profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserListings = async () => {
    try {
      setLoadingListings(true);
      const listings = await getUserListings();
      setUserListings(listings);
    } catch (err) {
      console.error('Error fetching user listings:', err);
    } finally {
      setLoadingListings(false);
    }
  };

  const loadActivityStats = async (userId: string) => {
    if (!userId) return;

    try {
      const { count: dinnersCount } = await supabase
        .from('dinner_participants')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'attended');

      const { count: friendsCount } = await supabase
        .from('friends_accepted')
        .select('*', { count: 'exact', head: true })
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`);

      const { count: blindDatesCount } = await supabase
        .from('blind_date_participants')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'attended');

      const daysActive = profile?.created_at
        ? Math.floor((Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24))
        : 0;

      setActivityStats({
        dinnersAttended: dinnersCount || 0,
        friendsCount: friendsCount || 0,
        blindDatesAttended: blindDatesCount || 0,
        daysActive
      });
    } catch (err) {
      console.error('Error loading activity stats:', err);
    }
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !profile) return;

    const file = e.target.files[0];
    if (file.size > 5 * 1024 * 1024) {
      setError(t('profile.pictureError') as string);
      return;
    }

    try {
      setUploadingPicture(true);
      setError(null);

      const fileExt = file.name.split('.').pop();
      const filePath = `${profile.id}/profile.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_picture: publicUrl })
        .eq('id', profile.id);

      if (updateError) throw updateError;

      setProfile(prev => prev ? { ...prev, profile_picture: publicUrl } : null);
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err instanceof Error ? err.message : t('profile.pictureError') as string);
    } finally {
      setUploadingPicture(false);
    }
  };

  if (loading) {
    return <LoadingIndicator message={t('common.loading')} />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <User className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('profile.notFound')}</h2>
          <p className="text-gray-600 mb-4">
            {t('profile.notFoundMessage')}
          </p>
          <Link
            to="/"
            className="inline-block bg-orange-500 text-white px-6 py-2 rounded-md hover:bg-orange-600 transition"
          >
            {t('profile.goHome')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative mb-12">
        <div 
          className="h-96 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-transparent" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <div className="relative mb-6">
                {profile.profile_picture ? (
                  <img
                    src={profile.profile_picture}
                    alt={profile.full_name || ''}
                    className="w-32 h-32 rounded-full border-4 border-white object-cover mx-auto shadow-xl"
                  />
                ) : (
                  <div className="w-32 h-32 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center mx-auto shadow-xl">
                    <User className="h-16 w-16 text-gray-400" />
                  </div>
                )}
                {isOwnProfile && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPicture}
                    className="absolute bottom-0 right-1/2 translate-x-12 translate-y-2 p-2 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition shadow-lg"
                    title={t('profile.changePicture') as string}
                  >
                    <Camera className="h-5 w-5" />
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleProfilePictureChange}
                  className="hidden"
                />
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">{profile.full_name}</h1>
              {profile.location && (
                <div className="flex items-center justify-center text-xl text-gray-200">
                  <MapPin className="h-5 w-5 mr-2" />
                  <span>{profile.location}</span>
                </div>
              )}
              {isOwnProfile ? (
                <div className="flex justify-center space-x-4 mt-6">
                  <Link
                    to="/profile-settings"
                    className="flex items-center space-x-2 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition"
                  >
                    <Settings className="h-5 w-5" />
                    <span>Profileinstellungen</span>
                  </Link>
                </div>
              ) : (
                <div className="flex justify-center space-x-4 mt-6">
                  <FriendRequestButton userId={profile.id} />
                  <DinnerRecommendationButton 
                    userId={profile.id}
                    userName={profile.full_name || undefined}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Bio */}
            {profile.bio && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('profile.about')}</h2>
                <p className="text-gray-600 leading-relaxed">{profile.bio}</p>
              </div>
            )}

            {/* User's Listings (Own Profile Only) */}
            {isOwnProfile && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Deine Einladungen</h2>
                  <Link 
                    to="/discover"
                    className="flex items-center space-x-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition"
                  >
                    <PlusCircle className="h-5 w-5" />
                    <span>Neue Einladung erstellen</span>
                  </Link>
                </div>
                
                {loadingListings ? (
                  <div className="flex justify-center py-8">
                    <Loader className="h-8 w-8 animate-spin text-orange-500" />
                  </div>
                ) : userListings.length > 0 ? (
                  <div className="space-y-4">
                    {userListings.map(listing => (
                      <div key={listing.id} className="border rounded-lg p-4 hover:shadow-md transition">
                        <div className="flex justify-between">
                          <div>
                            <h3 className="font-medium text-lg">{listing.venue_name}</h3>
                            <div className="text-gray-600 text-sm">
                              {new Date(listing.date).toLocaleDateString()} 
                              {listing.time && ` at ${listing.time.slice(0, 5)}`}
                            </div>
                          </div>
                          <div className="text-sm text-gray-500">
                            {new Date(listing.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="mt-2 text-gray-600">
                          {listing.preferences && (
                            <div className="text-sm"><strong>Präferenzen:</strong> {listing.preferences}</div>
                          )}
                          {listing.description && (
                            <div className="text-sm mt-1 line-clamp-2">{listing.description}</div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Du hast noch keine Dinner Einladungen erstellt</p>
                    <Link 
                      to="/discover"
                      className="mt-4 inline-block text-orange-500 hover:text-orange-600"
                    >
                      Erstelle deine erste Einladung
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Activity Stats */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.activityStats')}</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className={`text-center transition-all duration-500 transform hover:scale-105 ${animateStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="bg-orange-100 rounded-full p-4 mb-4 mx-auto w-16 h-16 flex items-center justify-center">
                    <Utensils className="h-8 w-8 text-orange-500" />
                  </div>
                  <div className="text-3xl font-bold text-orange-500">{activityStats.dinnersAttended}</div>
                  <div className="text-sm text-gray-600">{t('profile.dinnersAttended')}</div>
                </div>
                <div className={`text-center transition-all duration-500 transform hover:scale-105 delay-100 ${animateStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="bg-blue-100 rounded-full p-4 mb-4 mx-auto w-16 h-16 flex items-center justify-center">
                    <Users className="h-8 w-8 text-blue-500" />
                  </div>
                  <div className="text-3xl font-bold text-blue-500">{activityStats.friendsCount}</div>
                  <div className="text-sm text-gray-600">{t('profile.friends')}</div>
                </div>
                <div className={`text-center transition-all duration-500 transform hover:scale-105 delay-200 ${animateStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="bg-pink-100 rounded-full p-4 mb-4 mx-auto w-16 h-16 flex items-center justify-center">
                    <Heart className="h-8 w-8 text-pink-500" />
                  </div>
                  <div className="text-3xl font-bold text-pink-500">{activityStats.blindDatesAttended}</div>
                  <div className="text-sm text-gray-600">{t('profile.blindDates')}</div>
                </div>
                <div className={`text-center transition-all duration-500 transform hover:scale-105 delay-300 ${animateStats ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  <div className="bg-green-100 rounded-full p-4 mb-4 mx-auto w-16 h-16 flex items-center justify-center">
                    <Calendar className="h-8 w-8 text-green-500" />
                  </div>
                  <div className="text-3xl font-bold text-green-500">{activityStats.daysActive}</div>
                  <div className="text-sm text-gray-600">{t('profile.daysActive')}</div>
                </div>
              </div>
            </div>

            {/* Achievements */}
            {isOwnProfile && achievements.length > 0 && (
              <>
                <AchievementPoints achievements={achievements} />
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.achievements')}</h2>
                  <Achievements achievements={achievements} />
                </div>
              </>
            )}

            {/* Dining Preferences */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.diningPreferences')}</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {/* Favorite Cuisines */}
                {profile.favorite_cuisines && profile.favorite_cuisines.length > 0 && (
                  <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <ChefHat className="h-6 w-6 text-orange-500 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">{t('profile.favoriteCuisines')}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.favorite_cuisines.map((cuisine) => (
                        <span
                          key={cuisine}
                          className="px-3 py-1 bg-white/50 text-orange-700 rounded-full text-sm font-medium shadow-sm"
                        >
                          {cuisine}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Dietary Preferences */}
                {profile.dietary_preferences && profile.dietary_preferences.length > 0 && (
                  <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Utensils className="h-6 w-6 text-green-500 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">{t('profile.dietaryPreferences')}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.dietary_preferences.map((pref) => (
                        <span
                          key={pref}
                          className="px-3 py-1 bg-white/50 text-green-700 rounded-full text-sm font-medium shadow-sm"
                        >
                          {pref}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Range */}
                {profile.price_range && (
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <DollarSign className="h-6 w-6 text-blue-500 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">{t('profile.priceRange')}</h3>
                    </div>
                    <span className="px-3 py-1 bg-white/50 text-blue-700 rounded-full text-sm font-medium shadow-sm">
                      {profile.price_range}
                    </span>
                  </div>
                )}

                {/* Preferred Dining Times */}
                {profile.preferred_dining_times && profile.preferred_dining_times.length > 0 && (
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Clock className="h-6 w-6 text-purple-500 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">{t('profile.preferredDiningTimes')}</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {profile.preferred_dining_times.map((time) => (
                        <span
                          key={time}
                          className="px-3 py-1 bg-white/50 text-purple-700 rounded-full text-sm font-medium shadow-sm"
                        >
                          {time}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Drinking Preference */}
                {profile.drinking_preference && (
                  <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Beer className="h-6 w-6 text-amber-500 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">{t('profile.drinkingPreference')}</h3>
                    </div>
                    <span className="px-3 py-1 bg-white/50 text-amber-700 rounded-full text-sm font-medium shadow-sm">
                      {profile.drinking_preference.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </div>
                )}

                {/* Smoking Preference */}
                {profile.smoking_preference && (
                  <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <Flame className="h-6 w-6 text-red-500 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-900">{t('profile.smokingPreference')}</h3>
                    </div>
                    <span className="px-3 py-1 bg-white/50 text-red-700 rounded-full text-sm font-medium shadow-sm">
                      {profile.smoking_preference.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Languages and Interests */}
            <div className="grid md:grid-cols-2 gap-8">
              {/* Languages */}
              {profile.languages && profile.languages.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center mb-6">
                    <Globe className="h-7 w-7 text-blue-500 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-900">{t('profile.languages')}</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profile.languages.map((language) => (
                      <span
                        key={language}
                        className="px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium transform transition-all duration-300 hover:scale-105 hover:shadow-md"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Interests */}
              {profile.interests && profile.interests.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-8">
                  <div className="flex items-center mb-6">
                    <Coffee className="h-7 w-7 text-purple-500 mr-3" />
                    <h2 className="text-2xl font-bold text-gray-900">{t('profile.interests')}</h2>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {profile.interests.map((interest) => (
                      <span
                        key={interest}
                        className="px-4 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium transform transition-all duration-300 hover:scale-105 hover:shadow-md"
                      >
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Referral Link (Own Profile Only) */}
            {isOwnProfile && (
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('language.current') === 'en' ? 'Invite Restaurants' : 'Restaurants einladen'}</h2>
                <ReferralLink />
              </div>
            )}

            {/* Pending Friend Requests (Own Profile Only) */}
            {isOwnProfile && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.pendingRequests')}</h2>
                <PendingFriendRequests />
              </div>
            )}

            {/* Friends List */}
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.friends')}</h2>
              <Friends />
            </div>

            {/* Dinner Recommendations (Own Profile Only) */}
            {isOwnProfile && (
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('profile.recommendations')}</h2>
                <DinnerRecommendations />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;